import * as React from "react";
import { Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * InfoHint — a small "i" affordance that reveals explanatory text ON DEMAND.
 *
 * Use this instead of dumping descriptive paragraphs inline on the page.
 * The default state is quiet (just a muted icon); users click to learn more
 * only if they want to. This keeps screens scannable and fast to operate.
 *
 * Usage:
 *   <SectionHeader>
 *     Market Forecast Basis
 *     <InfoHint title="Market Forecast Basis">
 *       IHS/S&P Mobility vehicle volumes linked to this program. This is the
 *       first planning basis, before supplier take rate and content are applied.
 *     </InfoHint>
 *   </SectionHeader>
 *
 * Props:
 *   - children / content: the explanation (node or string). `children` wins.
 *   - title:        optional bold heading inside the popover.
 *   - learnMore:    optional { href, label } to deep-link to docs/a fuller view.
 *   - side/align:   popover placement (passthrough to Radix).
 *   - label:        accessible label for the trigger (default "More information").
 *   - className:    extra classes for the trigger button.
 *   - size:         icon size in px (default 14).
 */
export default function InfoHint({
  children,
  content,
  title,
  learnMore,
  side = "top",
  align = "center",
  label = "More information",
  className,
  size = 14,
}) {
  const body = children ?? content;

  // Stop clicks from bubbling to a parent card/row that has its own onClick,
  // and never submit a surrounding form.
  const swallow = (e) => e.stopPropagation();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={swallow}
          onPointerDown={swallow}
          className={cn(
            "inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full",
            "text-slate-400 hover:text-slate-600 focus:outline-none focus-visible:ring-2",
            "focus-visible:ring-blue-500/40 transition-colors align-middle",
            className
          )}
        >
          <Info style={{ width: size, height: size }} aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        onClick={swallow}
        className="w-64 p-3 text-left"
      >
        {title && (
          <div className="mb-1 text-xs font-semibold text-slate-900">{title}</div>
        )}
        <div className="text-xs leading-relaxed text-slate-600">{body}</div>
        {learnMore?.href && (
          <a
            href={learnMore.href}
            target={learnMore.href.startsWith("http") ? "_blank" : undefined}
            rel="noreferrer"
            onClick={swallow}
            className="mt-2 inline-block text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            {learnMore.label || "Learn more"}
          </a>
        )}
      </PopoverContent>
    </Popover>
  );
}
